import { makePaginatedSearch } from "api/makeApiRequestType/makePaginatedSearch";

import { convertEventDatesToJavascriptDates } from "api/utils/convertEventDatesToJavascriptDates";
import {
	flagValidationErrorsInSingleSessionPriceBase,
	SingleSessionPriceBase
} from "types-and-validators/SingleSessionPriceBase";
import {
	flagValidationErrorsInMeetingTimeSlotRawFromApi,
	MeetingTimeSlotRawFromApi
} from "types-and-validators/MeetingTimeSlotRawFromApi";
import {
	flagValidationErrorsInSessionType,
	SessionType
} from "types-and-validators/SessionType";
import {
	RequestToMentorRawFromApi,
	flagValidationErrorsInRequestToMentorRawFromApi
} from "types-and-validators/RequestToMentorRawFromApi";
import { formatDataForGetRequest } from "api/utils/formatDataForGetRequest";
import {
	ValidationCallback,
	validate,
	validationErrorsToString
} from "validation/validate";
import { FollowUpChecksParams } from "validation/getFieldChecker";
import { Medium } from "hooks/useAvailableMediums";
import { isOwnMeeting } from "utils/isOwnMeeting";

type RawApiOutputRow = {
	id: string;
	privateMessageContents?: string;
	privateMessageDatesentUTC?: string;
	privateMessageId?: string;
	singleSessionPrice?: SingleSessionPriceBase;
	sessionType: SessionType;
	medium: Medium;
} & RequestToMentorRawFromApi &
	MeetingTimeSlotRawFromApi;

const flagValidationErrorsInRow: ValidationCallback<RawApiOutputRow> = (
	input,
	utils,
	auth
) => {
	const { flag, checkField } = utils;

	checkField("id", { type: "string" });

	flagValidationErrorsInMeetingTimeSlotRawFromApi(input, utils, auth);

	let hostUidOK = false;
	checkField("hostUid", {
		type: "string",
		followUpChecks: () => {
			hostUidOK = true;
		}
	});

	let guestUidOK = false;
	checkField("guestUid", {
		type: "string",
		followUpChecks: () => {
			guestUidOK = true;
		}
	});

	if (hostUidOK && guestUidOK) {
		const { hostUid, guestUid } = input as RawApiOutputRow;
		if (isOwnMeeting({ auth, meeting: { hostUid, guestUid } })) {
			checkField("medium", {
				type: "string",
				followUpChecks: arg => {
					const { findInvalid, value } = arg as FollowUpChecksParams<"string">;
					if (!["google-meet", "skype", "zoom"].includes(value)) {
						findInvalid("Invalid value");
					}
				}
			});

			flagValidationErrorsInRequestToMentorRawFromApi(input, utils, auth);

			if (input.singleSessionPrice) {
				validate(input.singleSessionPrice, {
					doValidate: flagValidationErrorsInSingleSessionPriceBase,
					withErrors: errors => {
						flag("singleSessionPrice", validationErrorsToString(errors));
					},
					auth
				});
			}

			if (input.sessionType) {
				validate(input.sessionType, {
					doValidate: flagValidationErrorsInSessionType,
					withErrors: errors => {
						flag("sessionType", validationErrorsToString(errors));
					},
					auth
				});
			} else {
				flag("sessionType", "missing");
			}
		}
	}
};

export interface OutputRow
	extends Omit<
		RawApiOutputRow,
		"dateStartsUTC" | "dateEndsJustBeforeUTC" | "privateMessageDatesentUTC"
	> {
	dateStartsUTC: Date;
	dateEndsJustBeforeUTC: Date;
	privateMessageDatesentUTC?: Date;
}

export type Params = {
	hostUid?: string;
	guestUid?: string;
	earliestStartDateUTC?: Date;
	mustStartBeforeUTC?: Date;
	mustEndAfterUTC?: Date;
	mustEndBeforeUTC?: Date;
	isAccepted?: boolean;
	isRejected?: boolean;
};

export const getMeetingRequests = makePaginatedSearch<
	Params,
	RawApiOutputRow,
	OutputRow
>({
	endPoint: "meetingRequests",
	flagValidationErrorsInRow,
	postProcessor: convertEventDatesToJavascriptDates,
	preProcessor: formatDataForGetRequest
});
